// <copyright file="CheckoutPDFDownloads.ext.tsx" company="clarity-ventures.com">
// Copyright (c) clarity-ventures.com. All rights reserved.
// </copyright>
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { Button } from "@core/components/ui/button";
import { Surface } from "@core/components/ui/surface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faRotate, faSpinner } from "@fortawesome/pro-light-svg-icons";
import type { SalesCollection } from "@/apidtos";

interface Props {
  order: SalesCollection;
}

const MASTER_TYPE = "CheckoutMasterReport";
const ITEM_TYPE = "CheckoutPrescription";
const PACKING_TYPE = "CheckoutPackingSlip";

const CheckoutPDFDownloads = ({ order }: Props) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = ["checkout-pdfs.order", order.Id];

  const detail = useQuery({
    queryKey,
    queryFn: () =>
      api.GetSalesCollectionById(order.Id, [
        "SalesCollectionFiles.Secondary",
        "Fulfillments.Files.File",
        "Fulfillments.Items",
      ]),
  });

  const regenAll = useMutation({
    mutationFn: () => api.RegenerateCheckoutPDFs(order.Id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
  const regenMaster = useMutation({
    mutationFn: () => api.RegenerateMasterCheckoutPDF(order.Id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const data = detail.data ?? order;
  const masterFiles = (data.SalesCollectionFiles ?? [])
    .map((j) => j.Secondary)
    .filter((f): f is NonNullable<typeof f> => !!f && f.TypeId === MASTER_TYPE);
  const packingSlipFiles = (data.SalesCollectionFiles ?? [])
    .map((j) => j.Secondary)
    .filter((f): f is NonNullable<typeof f> => !!f && f.TypeId === PACKING_TYPE);

  const prescriptionFiles = ((data as any).Fulfillments ?? [])
    .filter((f: any) => f?.IsActive !== false && !f?.IsDeleted)
    .flatMap((f: any) =>
      (f.Files ?? [])
        .filter((ff: any) => ff?.IsActive !== false && !ff?.IsDeleted)
        .map((ff: any) => ff.File ?? ff.Secondary)
        .filter((file: any) => !!file && file.TypeId === ITEM_TYPE),
    )
    .sort((a: any, b: any) =>
      String(a.OriginalFileName ?? "").localeCompare(String(b.OriginalFileName ?? "")),
    );

  return (
    <Surface className="p-4 my-2 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">
          <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
          Checkout PDFs
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => regenAll.mutate()}
          disabled={regenAll.isPending}
        >
          <FontAwesomeIcon
            icon={regenAll.isPending ? faSpinner : faRotate}
            spin={regenAll.isPending}
            className="mr-2"
          />
          Regenerate all
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Master report</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => regenMaster.mutate()}
            disabled={regenMaster.isPending}
          >
            <FontAwesomeIcon
              icon={regenMaster.isPending ? faSpinner : faRotate}
              spin={regenMaster.isPending}
              className="mr-1"
            />
            Regenerate
          </Button>
        </div>
        {masterFiles.length === 0 ? (
          <p className="text-sm text-gray-500">No master report generated yet.</p>
        ) : (
          <ul className="list-disc ml-6">
            {masterFiles.map((f) => (
              <li key={f.Id}>
                <a className="text-blue-600 underline" href={`/api/file/${f.Id}`} download>
                  {f.OriginalFileName ?? `Master report #${f.Id}`}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-1">Packing slips</h4>
        {packingSlipFiles.length === 0 ? (
          <p className="text-sm text-gray-500">No packing slips generated yet.</p>
        ) : (
          <ul className="list-disc ml-6">
            {packingSlipFiles.map((f) => (
              <li key={f.Id}>
                <a className="text-blue-600 underline" href={`/api/file/${f.Id}`} download>
                  {f.OriginalFileName ?? `Packing slip #${f.Id}`}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-1">Prescriptions</h4>
        {prescriptionFiles.length === 0 ? (
          <p className="text-sm text-gray-500">No prescription PDFs generated yet.</p>
        ) : (
          <ul className="list-disc ml-6">
            {prescriptionFiles.map((file: any) => (
              <li key={file.Id}>
                <a className="text-blue-600 underline" href={`/api/file/${file.Id}`} download>
                  {file.OriginalFileName ?? `Prescription #${file.Id}`}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Surface>
  );
};

registerExtension("admin.sales.orders.$id.tools", CheckoutPDFDownloads, "CheckoutPDFDownloads");
